import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Car, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  make: "", model: "", year: "", license_plate: "", vin: "", current_mileage: 0,
  oil_change_due_mileage: "", tire_rotation_due_mileage: "", insurance_expiration: "",
  registration_expiration: "", assigned_instructor_id: "", status: "available",
};

const NUMERIC_FIELDS = ["year", "current_mileage", "oil_change_due_mileage", "tire_rotation_due_mileage"];

export default function Vehicles() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    try {
      const [vRes, iRes] = await Promise.all([api.get("/vehicles"), api.get("/instructors")]);
      setVehicles(vRes.data);
      setInstructors(iRes.data);
    } catch {
      toast.error("Could not load fleet");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    if (!form.make || !form.model || !form.license_plate) {
      toast.error("Make, model, and license plate are required");
      return;
    }
    try {
      const payload = { ...form };
      NUMERIC_FIELDS.forEach((k) => {
        payload[k] = payload[k] === "" ? null : parseFloat(payload[k]);
      });
      await api.post("/vehicles", payload);
      toast.success("Vehicle added to fleet");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add vehicle");
    }
  };

  return (
    <div className="space-y-5" data-testid="vehicles-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Manage your fleet, maintenance due dates, and instructor assignments.</p>
        <Button onClick={() => setOpen(true)} className="bg-[hsl(var(--role-accent))] hover:opacity-95" data-testid="add-vehicle-button">
          <Plus className="h-4 w-4 mr-2" /> Add Vehicle
        </Button>
      </div>

      {loading ? (
        <Skeleton className="h-64 rounded-[var(--radius)]" />
      ) : vehicles.length === 0 ? (
        <div className="rounded-[var(--radius)] border bg-card p-12 text-center space-y-2" data-testid="vehicles-empty-state">
          <Car className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No vehicles in the fleet yet.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {vehicles.map((v) => (
            <div key={v.id} className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 space-y-2 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/vehicles/${v.id}`)} data-testid="vehicle-card">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold font-heading">{v.make} {v.model} {v.year ? `(${v.year})` : ""}</h3>
                <StatusBadge status={v.status} />
              </div>
              <p className="text-xs text-muted-foreground font-mono-ui">{v.license_plate}</p>
              <p className="text-xs text-muted-foreground">Mileage: {v.current_mileage?.toLocaleString() || 0} mi</p>
              <p className="text-xs text-muted-foreground">Assigned to: {v.assigned_instructor_name || "Unassigned"}</p>
              {v.open_issues_count > 0 && (
                <p className="text-xs text-destructive font-medium flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> {v.open_issues_count} open issue(s)</p>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="add-vehicle-dialog">
          <DialogHeader>
            <DialogTitle>Add Vehicle</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="space-y-1.5">
              <Label>Make</Label>
              <Input value={form.make} onChange={(e) => setForm((f) => ({ ...f, make: e.target.value }))} data-testid="vehicle-form-make-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Model</Label>
              <Input value={form.model} onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))} data-testid="vehicle-form-model-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Year</Label>
              <Input type="number" value={form.year} onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>License Plate</Label>
              <Input value={form.license_plate} onChange={(e) => setForm((f) => ({ ...f, license_plate: e.target.value }))} data-testid="vehicle-form-plate-input" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>VIN (optional)</Label>
              <Input value={form.vin} onChange={(e) => setForm((f) => ({ ...f, vin: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Current mileage</Label>
              <Input type="number" value={form.current_mileage} onChange={(e) => setForm((f) => ({ ...f, current_mileage: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                <option value="available">Available</option>
                <option value="in_use">In Use</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Oil change due (mileage)</Label>
              <Input type="number" value={form.oil_change_due_mileage} onChange={(e) => setForm((f) => ({ ...f, oil_change_due_mileage: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Tire rotation due (mileage)</Label>
              <Input type="number" value={form.tire_rotation_due_mileage} onChange={(e) => setForm((f) => ({ ...f, tire_rotation_due_mileage: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Insurance expiration</Label>
              <Input type="date" value={form.insurance_expiration} onChange={(e) => setForm((f) => ({ ...f, insurance_expiration: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Registration expiration</Label>
              <Input type="date" value={form.registration_expiration} onChange={(e) => setForm((f) => ({ ...f, registration_expiration: e.target.value }))} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Assign instructor (optional)</Label>
              <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={form.assigned_instructor_id} onChange={(e) => setForm((f) => ({ ...f, assigned_instructor_id: e.target.value }))}>
                <option value="">Unassigned</option>
                {instructors.map((i) => (
                  <option key={i.id} value={i.id}>{i.full_name}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submit} data-testid="vehicle-form-submit-button">Save Vehicle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
